import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { getPlatformColor } from "../PlatformBadge";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";

interface PlatformSpendChartProps {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function PlatformSpendChart({ isEditMode, onRemove, onResize, onResetSize }: PlatformSpendChartProps) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["platform-breakdown", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("platform-breakdown", params));
      if (!res.ok) throw new Error("Failed to fetch platform breakdown");
      return res.json() as Promise<Array<{
        platformSlug: string;
        platformName: string;
        platformColor: string;
        spend: number;
        impressions: number;
        conversions: number;
        conversionValue: number;
      }>>;
    },
    enabled: sourceType === "unified",
  });

  const chartData = data?.filter(d => d.spend > 0).map(d => ({
    name: d.platformName,
    value: Math.round(d.spend * 100) / 100,
    color: getPlatformColor(d.platformSlug),
  })) || [];

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <DashboardWidget
      title="Spend by Platform"
      widgetType="chart"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
      platformSlug="unified"
    >
      <div className="p-4 h-full flex flex-col" data-testid="chart-platform-spend">
        {chartData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "#5F6D82" }}>
            No platform data available
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="50%"
                  outerRadius="80%"
                  paddingAngle={2}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`$${value.toLocaleString()}`, "Spend"]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend
                  formatter={(value: string) => {
                    const item = chartData.find(d => d.name === value);
                    const pct = item && total > 0 ? Math.round((item.value / total) * 100) : 0;
                    return `${value} (${pct}%)`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </DashboardWidget>
  );
}
